import { useEffect } from 'react'
import { supabase } from '@/lib/supabase'

/**
 * ExtensionAuthBridge
 * 
 * This component acts as a bridge between the browser extension and the web app.
 * It listens for authentication tokens from the extension via postMessage and
 * sets up the Supabase session accordingly.
 * 
 * Security: Multi-layer validation ensures only legitimate extension messages are processed.
 * 
 * This component renders nothing and only runs when fromExtension=1 is in the URL.
 */
export function ExtensionAuthBridge() {
  useEffect(() => {
    // Check if we should be active
    const urlParams = new URLSearchParams(window.location.search)
    const fromExtension = urlParams.get('fromExtension')
    
    if (fromExtension !== '1') {
      return
    }

    // Store in sessionStorage so we remember even when navigating to other pages
    sessionStorage.setItem('calintent_in_extension', 'true')
    console.log('[Calintent iframe] 🔐 Extension mode active')

    const handleMessage = async (event: MessageEvent) => {
      // ========================================
      // 🔐 SECURITY LAYER 1: Origin Validation
      // ========================================
      const isFromExtension = event.origin.startsWith('chrome-extension://')
      const isFromApp = event.origin === 'https://app.calintent.com' || 
                        event.origin === 'https://app.dev.calintent.com' ||
                        event.origin === window.location.origin
      
      if (!isFromExtension && !isFromApp) {
        console.warn('[Calintent iframe] ❌ Security: Message from untrusted origin:', event.origin)
        return
      }

      // ========================================
      // Handle Logout Event
      // ========================================
      if (event.data?.type === 'CALINTENT_LOGOUT') {
        console.log('[Calintent iframe] 🚪 Logout')
        try {
          await supabase.auth.signOut({ scope: 'local' })
          window.location.reload()
        } catch (err) {
          console.error('[Calintent iframe] ❌ Logout failed:', err)
        }
        return
      }

      // ========================================
      // Handle Authentication Token Message
      // ========================================
      if (event.data?.type === 'CALINTENT_SET_TOKEN') {
        // ========================================
        // 🔐 SECURITY LAYER 2: Message Structure Validation
        // ========================================
        const accessToken = event.data.accessToken
        const refreshToken = event.data.refreshToken

        if (!accessToken || !refreshToken) {
          console.error('[Calintent iframe] ❌ Security: Invalid token structure. Missing tokens.')
          console.error('[Calintent iframe] Expected: { accessToken, refreshToken }, got:', event.data)
          return
        }

        console.log('[Calintent iframe] 🔑 Setting session')
        console.log('[Calintent iframe]   Access token:', accessToken.length, 'chars')
        console.log('[Calintent iframe]   Refresh token:', refreshToken.length, 'chars')

        try {
          // Set the Supabase session with BOTH tokens
          const { data, error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken
          })

          if (error) {
            console.error('[Calintent iframe] ❌ Failed to set session:', {
              error: error.message,
              status: error.status,
              name: error.name
            })
            
            // Notify extension of failure (only if from extension)
            if (event.source && isFromExtension) {
              (event.source as Window).postMessage({
                type: 'CALINTENT_AUTH_RESULT',
                success: false,
                error: error.message
              }, event.origin)
            }
          } else {
            console.log('[Calintent iframe] ✅ Session set successfully!')
            console.log('[Calintent iframe]   User:', data.user?.email || data.user?.id)
            
            // Notify extension of success (only if from extension)
            if (event.source && isFromExtension) {
              (event.source as Window).postMessage({
                type: 'CALINTENT_AUTH_RESULT',
                success: true,
                user: {
                  id: data.user?.id,
                  email: data.user?.email
                }
              }, event.origin)
            }
          }
        } catch (err) {
          console.error('[Calintent iframe] ❌ Exception while setting session:', err)
          
          // Notify extension of exception (only if from extension)
          if (event.source && isFromExtension) {
            (event.source as Window).postMessage({
              type: 'CALINTENT_AUTH_RESULT',
              success: false,
              error: err instanceof Error ? err.message : 'Unknown error'
            }, event.origin)
          }
        }
      }
    }

    // Add the event listener
    window.addEventListener('message', handleMessage)

    // Notify extension that we're ready
    if (window.parent !== window) {
      window.parent.postMessage({
        type: 'CALINTENT_IFRAME_READY'
      }, '*')
    }

    // Cleanup function
    return () => {
      window.removeEventListener('message', handleMessage)
    }
  }, [])

  // Render nothing - this is an invisible bridge component
  return null
}

